import { useMemo } from 'react';

import { useGetPaTypeLookups } from '@/api/pa-types';
import { useGetPools } from '@/api/use-pools';
import { useGetStates } from '@/api/use-states';

import { useTherapeuticClassFilters } from './use-therapeutic-class-filters';

/**
 * Hook for constructing therapeutic class report API parameters from filter state.
 * 
 * Handles the case where both pools and individual states are selected by filtering
 * states to only include those that belong to the selected pools, preventing
 * contradictory API calls that would return no data.
 */

interface UseTherapeuticClassReportParamsOptions {
  classificationId?: number;
  drugIds?: number[];
  forceEnabled?: boolean;
}

export const useTherapeuticClassReportParams = ({
  classificationId,
  drugIds = [],
  forceEnabled = false,
}: UseTherapeuticClassReportParamsOptions) => {
  const { data: states = [] } = useGetStates();
  const { data: pools = [] } = useGetPools();
  const { data: paTypes = [] } = useGetPaTypeLookups();
  const { filter: therapeuticClassFilter } = useTherapeuticClassFilters();

  // Get selected filters from filter state
  const selectedStatesFilter = therapeuticClassFilter.values.find((v: any) => v.key === 'state')?.value || [];
  const selectedPoolsFilter = therapeuticClassFilter.values.find((v: any) => v.key === 'pool')?.value || [];
  const selectedPaTypesFilter = therapeuticClassFilter.values.find((v: any) => v.key === 'paType')?.value || [];
  const selectedPdlStatusesFilter = therapeuticClassFilter.values.find((v: any) => v.key === 'pdlStatus')?.value || [];
  const allSelectedStatesAndPools = [...selectedStatesFilter, ...selectedPoolsFilter];

  // Prepare parameters for therapeutic class report API
  const reportParams = useMemo(() => {
    if (!classificationId) return null;
    
    // Get valid state and pool IDs
    const validStateIds = selectedStatesFilter
      .filter((state: any) => states.some(s => s.code === state.code))
      .map((state: any) => {
        const stateObj = states.find(s => s.code === state.code);
        return stateObj?.id;
      })
      .filter(Boolean) as number[];

    const validPoolIds = selectedPoolsFilter
      .filter((pool: any) => pools.some(p => p.code === pool.code))
      .map((pool: any) => {
        const poolObj = pools.find(p => p.code === pool.code);
        return poolObj?.id;
      })
      .filter(Boolean) as number[];

    // If pools are selected, filter states to only include those in the selected pools
    let finalStateIds = validStateIds;
    if (validPoolIds.length > 0) {
      const statesInSelectedPools = new Set<number>();
      validPoolIds.forEach(poolId => {
        const pool = pools.find(p => p.id === poolId);
        pool?.states.forEach(state => {
          if (state.id) {
            statesInSelectedPools.add(state.id);
          }
        });
      });
      
      // Only include states that are in the selected pools
      finalStateIds = validStateIds.filter(stateId => statesInSelectedPools.has(stateId));
    }

    // Get valid PA Type IDs
    // Note: The filter uses hardcoded codes, but the API returns lookups with IDs
    // We need to map the filter codes to API lookup IDs by label matching
    const validPaTypeIds = selectedPaTypesFilter
      .map((paType: any) => {
        // Find the API lookup that matches the filter option by label
        const paTypeObj = paTypes.find(pt => {
          // Map filter codes to labels for matching
          const labelMap: Record<string, string> = {
            'GeneralPdl': 'General PDL',
            'GeneralNonPdl': 'General Non-PDL',
            'ClassSpecific': 'Class-Specific',
            'DrugSpecific': 'Drug-Specific',
            'Oncology': 'Oncology'
          };
          return pt.label === labelMap[paType.code];
        });
        return paTypeObj?.id;
      })
      .filter(Boolean) as number[];

    // Get valid PDL Statuses (these are string values from the enum)
    const validPdlStatuses = selectedPdlStatusesFilter
      .map((pdlStatus: any) => pdlStatus.code)
      .filter(Boolean) as string[];
    
    const params = {
      classificationId,
      drugIds,
      stateIds: finalStateIds,
      poolIds: validPoolIds,
      paTypeIds: validPaTypeIds,
      pdlStatuses: validPdlStatuses
    };
    
    return params;
  }, [classificationId, drugIds, selectedStatesFilter, selectedPoolsFilter, selectedPaTypesFilter, selectedPdlStatusesFilter, states, pools, paTypes]);

  // Determine if API call should be enabled
  const enableApiCall = useMemo(() => {
    if (forceEnabled && reportParams) return true;
    
    const hasClassification = !!classificationId;
    const hasDrugs = drugIds.length > 0;
    // Since API returns all data when no filters are selected, we don't require states/pools to be selected
    
    return !!reportParams && hasClassification && hasDrugs;
  }, [classificationId, drugIds, reportParams, forceEnabled]);

  return {
    reportParams,
    enableApiCall,
    selectedStatesAndPools: allSelectedStatesAndPools,
    therapeuticClassFilter,
  };
};
